// unifiedNodeTabKeys.js

export const UNIFIED_TAB_KEYS = Object.freeze({
  ABOUT: '__ABOUT__',
  ACTIVITY: '__ACTIVITY__',
  FORMS: '__FORMS__',
  HIGHLIGHT: '__HIGHLIGHT__',
  WORKFLOW: '__WORKFLOW__',
  FLOOR_PLAN: '__FLOOR_PLAN__',
  // Legacy Peek keys — still reserved so they are never treated as related-type tabs.
  COMMENTS: '__COMMENTS__',
  HISTORY: '__HISTORY__',
  // Legacy Missions tab key — normalize onto WORKFLOW.
  MISSIONS: '__WORKFLOW__',
});

export const ADD_MODAL_SPECIAL_KEYS = Object.freeze({
  DATABASE: '__ADD_DATABASE__',
  CHANNEL: '__ADD_CHANNEL__',
  MESSAGE: '__ADD_MESSAGE__',
  DASHBOARD: '__ADD_DASHBOARD__',
});

const SPECIAL_TAB_KEY_SET = new Set(Object.values(UNIFIED_TAB_KEYS));

export const UNIFIED_TAB_META = Object.freeze({
  [UNIFIED_TAB_KEYS.ABOUT]: {
    iconKey: 'info',
    description: 'Metadata and details for this record.',
  },
  [UNIFIED_TAB_KEYS.ACTIVITY]: {
    iconKey: 'chat',
    description: 'Comments and change history for this record.',
  },
  [UNIFIED_TAB_KEYS.FORMS]: {
    iconKey: 'dynamic_form',
    description: 'Focused forms for targeted data entry on this record.',
  },
  [UNIFIED_TAB_KEYS.WORKFLOW]: {
    iconKey: 'account_tree',
    description: 'Choose and run playbooks for this record.',
  },
  [UNIFIED_TAB_KEYS.FLOOR_PLAN]: {
    iconKey: 'map',
    description: 'Lightweight floor plan for placing purchase order items by room.',
  },
});

/**
 * Maps legacy Peek Comments/History session keys onto Activity.
 *
 * @param {string|null|undefined} tabKey
 * @returns {string}
 */
export function normalizeUnifiedTabKey(tabKey) {
  const normalized = typeof tabKey === 'string' ? tabKey.trim() : '';
  if (!normalized) return '';
  if (
    normalized === UNIFIED_TAB_KEYS.COMMENTS
    || normalized === UNIFIED_TAB_KEYS.HISTORY
  ) {
    return UNIFIED_TAB_KEYS.ACTIVITY;
  }
  if (normalized === '__MISSIONS__' || normalized === 'missions') {
    return UNIFIED_TAB_KEYS.WORKFLOW;
  }
  return normalized;
}

/**
 * Returns true when the tab key represents a related node type (not a reserved unified tab).
 *
 * @param {string|null|undefined} tabKey
 * @returns {boolean}
 */
export function isRelatedTypeTabKey(tabKey) {
  const normalized = typeof tabKey === 'string' ? tabKey.trim() : '';
  if (!normalized) return false;
  return !SPECIAL_TAB_KEY_SET.has(normalized);
}

/**
 * Returns true when the tab key is one of the reserved unified tab keys.
 *
 * @param {string|null|undefined} tabKey
 * @returns {boolean}
 */
export function isUnifiedSpecialTabKey(tabKey) {
  const normalized = typeof tabKey === 'string' ? tabKey.trim() : '';
  if (!normalized) return false;
  return SPECIAL_TAB_KEY_SET.has(normalized);
}

const SESSION_TAB_STORAGE_PREFIX = 'nodal:nodeTab:';

/**
 * Reads the persisted tab key for a node from session storage.
 *
 * @param {string|null|undefined} nodeKey
 * @returns {string|null}
 */
export function getSessionTabKey(nodeKey) {
  if (!nodeKey) return null;

  try {
    const value = sessionStorage.getItem(`${SESSION_TAB_STORAGE_PREFIX}${nodeKey}`);
    if (typeof value !== 'string' || !value.trim()) return null;
    return normalizeUnifiedTabKey(value.trim()) || null;
  } catch {
    return null;
  }
}

/**
 * Persists the active tab key for a node in session storage.
 *
 * @param {string|null|undefined} nodeKey
 * @param {string|null|undefined} tabKey
 */
export function setSessionTabKey(nodeKey, tabKey) {
  if (!nodeKey || !tabKey) return;

  try {
    const normalized = normalizeUnifiedTabKey(tabKey) || tabKey;
    sessionStorage.setItem(`${SESSION_TAB_STORAGE_PREFIX}${nodeKey}`, normalized);
  } catch {
    // Ignore storage failures.
  }
}

/**
 * Resolves the default unified tab key for a node visit.
 *
 * Rule (no session memory):
 * 1. Playbooks when preferWorkflowTab (type has playbook templates) and the tab is available
 * 2. Highlight when enabled for the type
 * 3. About/Settings when preferAboutTab is set
 * 4. Details (Forms) when includeFormsTab is set
 * 5. First related type tab when related items exist
 *
 * @param {object} params
 * @param {object|null} params.nodeType
 * @param {string[]} params.visibleTypeKeys
 * @param {string|null} [params.sessionTabKey] - Ignored (kept for call-site compat).
 * @param {boolean} [params.includeAboutTab] - Include About (PeekPanel only).
 * @param {boolean} [params.includeActivityTab] - Include Activity (Peek platform chrome).
 * @param {boolean} [params.includeFormsTab] - Include Forms (main node surface).
 * @param {boolean} [params.includeWorkflowTab] - Include Playbooks when type has templates.
 * @param {boolean} [params.preferWorkflowTab] - Default to Playbooks when the type has templates.
 * @param {boolean} [params.preferAboutTab] - Default to About/Settings when Highlight is unavailable.
 * @returns {string|null}
 */
export function resolveDefaultUnifiedTabKey({
  nodeType,
  visibleTypeKeys = [],
  sessionTabKey: _sessionTabKey,
  includeAboutTab = false,
  includeActivityTab = false,
  includeFormsTab = true,
  includeWorkflowTab = false,
  preferWorkflowTab = false,
  preferAboutTab = false,
}) {
  if (preferWorkflowTab && includeWorkflowTab) {
    return UNIFIED_TAB_KEYS.WORKFLOW;
  }

  if (nodeType?.showHighlight) {
    return UNIFIED_TAB_KEYS.HIGHLIGHT;
  }

  if (preferAboutTab && includeAboutTab) {
    return UNIFIED_TAB_KEYS.ABOUT;
  }

  if (includeFormsTab) {
    return UNIFIED_TAB_KEYS.FORMS;
  }

  if (visibleTypeKeys[0]) {
    return visibleTypeKeys[0];
  }

  if (includeAboutTab) {
    return UNIFIED_TAB_KEYS.ABOUT;
  }

  if (includeWorkflowTab) {
    return UNIFIED_TAB_KEYS.WORKFLOW;
  }

  if (includeActivityTab) {
    return UNIFIED_TAB_KEYS.ACTIVITY;
  }

  // Never return Forms when that tab is hidden — callers (e.g. mirrored Node)
  // would fight a redirect loop against an unavailable About/Forms pair.
  return null;
}
